import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Download,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  Lightbulb,
  Target,
  ClipboardCheck,
  FileText,
  Loader2,
  Sparkles,
  Shield,
  LogIn,
  Gift,
  UserPlus,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BellCurveIndicator from '../components/BellCurveIndicator';
import SpiderChart from '../components/SpiderChart';
import TombolaModal from '../components/TombolaModal';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { assessmentsAPI, pdfAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

const AssessmentResults = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const { t, language } = useLanguage();
  const reportRef = useRef(null);
  const [benchmark, setBenchmark] = useState(null);
  const [loadingBenchmark, setLoadingBenchmark] = useState(true);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [animatedScore, setAnimatedScore] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [animationComplete, setAnimationComplete] = useState(false);
  const [showTombola, setShowTombola] = useState(false);
  const tombolaVisualType = searchParams.get('tombola');

  // Trigger entrance animations
  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Debug: Log what we receive from navigation state
  useEffect(() => {
    console.log('AssessmentResults - location.state:', location.state);
    console.log('AssessmentResults - results:', location.state?.results);
    console.log('AssessmentResults - overall_score:', location.state?.results?.overall_score);
  }, [location.state]);

  // NEW: 4 categories based on meeting decisions
  // Get results from location state or use demo data - memoized to prevent re-renders
  const results = React.useMemo(() => {
    return location.state?.results || {
      id: 'demo-assessment',
      name: 'Demo User',
      email: 'demo@company.com',
      company: 'Demo Company',
      overall_score: 62,
      dimension_scores: [
        { dimension: 'Strategie', dimension_en: 'Strategy', score: 65, level: 'Developing' },
        { dimension: 'Governance', dimension_en: 'Governance', score: 55, level: 'Developing' },
        { dimension: 'Technologie & Data', dimension_en: 'Technology & Data', score: 58, level: 'Developing' },
        { dimension: 'Mensen & Change', dimension_en: 'People & Change', score: 68, level: 'Developing' },
      ],
      status: 'completed',
      created_at: new Date().toISOString(),
    };
  }, [location.state?.results]);
  
  const userInfo = location.state?.userInfo || {
    companyName: 'Demo Company',
    name: 'Demo User',
    email: 'demo@company.com',
    industry: 'technology',
    companySize: '201-500',
    jobTitle: 'HR Director',
  };

  useEffect(() => {
    fetchBenchmark();
  }, []);

  // Animate the overall score - only run once when results are available
  useEffect(() => {
    if (isVisible && results?.overall_score && !hasAnimated) {
      const targetScore = results.overall_score;
      const duration = 1200; // 1.2 seconds - faster
      const steps = 40;
      const increment = targetScore / steps;
      let current = 0;
      
      setHasAnimated(true);
      
      const timer = setInterval(() => {
        current += increment;
        if (current >= targetScore) {
          setAnimatedScore(targetScore);
          setAnimationComplete(true);
          clearInterval(timer);
        } else {
          setAnimatedScore(Math.round(current));
        }
      }, duration / steps);
      
      return () => clearInterval(timer);
    }
  }, [isVisible, results?.overall_score, hasAnimated]);
  
  // Use the actual score from results - animation is just visual
  // If animation is complete, show animated score, otherwise show actual score to prevent display bugs
  const displayScore = animationComplete 
    ? animatedScore 
    : (results?.overall_score || 0);

  const fetchBenchmark = async () => {
    try {
      const data = await assessmentsAPI.getBenchmark();
      setBenchmark(data);
    } catch (error) {
      console.error('Failed to fetch benchmark:', error);
      // Use default benchmark (4 new categories)
      setBenchmark({
        total_assessments: 200,
        industry_avg: 58,
        dimension_benchmarks: {
          'Strategy': { avg: 55, top_quartile: 76 },
          'Strategie': { avg: 55, top_quartile: 76 },
          'Governance': { avg: 52, top_quartile: 74 },
          'Technology & Data': { avg: 54, top_quartile: 75 },
          'Technologie & Data': { avg: 54, top_quartile: 75 },
          'People & Change': { avg: 56, top_quartile: 77 },
          'Mensen & Change': { avg: 56, top_quartile: 77 },
        },
      });
    } finally {
      setLoadingBenchmark(false);
    }
  };

  // NEW: 6-point scale maturity levels (max score is now 6, not 5)
  const getMaturityLevel = (score) => {
    // Score is already a percentage (0-100)
    if (score >= 80) return { level: language === 'nl' ? 'Leidend' : 'Leading', color: 'text-green-600', bg: 'bg-green-100' };
    if (score >= 60) return { level: language === 'nl' ? 'Gevorderd' : 'Advanced', color: 'text-blue-600', bg: 'bg-blue-100' };
    if (score >= 40) return { level: language === 'nl' ? 'Ontwikkelend' : 'Developing', color: 'text-yellow-600', bg: 'bg-yellow-100' };
    if (score >= 20) return { level: language === 'nl' ? 'Opkomend' : 'Emerging', color: 'text-blue-600', bg: 'bg-blue-100' };
    return { level: language === 'nl' ? 'Beginnend' : 'Beginning', color: 'text-red-600', bg: 'bg-red-100' };
  };

  const getBenchmarkComparison = (score, benchmarkAvg) => {
    const diff = score - benchmarkAvg;
    if (diff >= 5) return { icon: TrendingUp, color: 'text-green-500', text: t('aboveAverage', { diff: `+${diff}` }) };
    if (diff <= -5) return { icon: TrendingDown, color: 'text-red-500', text: t('belowAverage', { diff }) };
    return { icon: Minus, color: 'text-gray-500', text: t('atAverage') };
  };

  // Calculate percentile based on score and benchmark
  // Uses a more realistic calculation based on assumed normal distribution
  const calculatePercentile = (score, avgScore) => {
    // If score is significantly below benchmark, percentile should be low
    // If score equals benchmark, percentile should be around 50
    // If score is significantly above benchmark, percentile should be high
    
    // Assumed standard deviation of scores (typically 15-20 points)
    const stdDev = 18;
    
    // Calculate z-score (how many standard deviations from mean)
    const zScore = (score - avgScore) / stdDev;
    
    // Convert z-score to percentile using cumulative distribution function approximation
    // This uses the error function approximation for normal distribution
    const t = 1 / (1 + 0.2316419 * Math.abs(zScore));
    const d = 0.3989423 * Math.exp(-zScore * zScore / 2);
    const probability = d * t * (0.3193815 + t * (-0.3565638 + t * (1.781478 + t * (-1.821256 + t * 1.330274))));
    
    let percentile;
    if (zScore > 0) {
      percentile = Math.round((1 - probability) * 100);
    } else {
      percentile = Math.round(probability * 100);
    }
    
    // Clamp between 1 and 99
    return Math.max(1, Math.min(99, percentile));
  };

  const generatePDF = async () => {
    setGeneratingPdf(true);
    try {
      // Use the backend PDF generator for a professional report
      // Include benchmark data for consistency
      const pdfData = {
        id: results.id,
        name: userInfo?.name || results.name,
        company: userInfo?.companyName || userInfo?.company || results.company,
        overall_score: results.overall_score,
        dimension_scores: results.dimension_scores,
        benchmark_avg: benchmark?.industry_avg || 52,
        created_at: new Date().toISOString(),
      };
      
      const blob = await pdfAPI.generateFromData(pdfData);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `HR_AI_Maturity_Report_${userInfo?.company || 'Quickscan'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      // Fallback to client-side PDF generation
      try {
        const html2pdf = (await import('html2pdf.js')).default;
        const element = reportRef.current;
        
        const opt = {
          margin: [10, 10, 10, 10],
          filename: `AI-Maturity-Report-${userInfo?.company || 'Quickscan'}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
        };
        
        await html2pdf().set(opt).from(element).save();
      } catch (fallbackError) {
        console.error('Fallback PDF also failed:', fallbackError);
        alert('Failed to generate PDF. Please try again.');
      }
    } finally {
      setGeneratingPdf(false);
    }
  };

  if (!results) {
    return null;
  }

  const overallScore = results.overall_score;
  const maturity = getMaturityLevel(overallScore);
  const benchmarkComparison = benchmark ? getBenchmarkComparison(overallScore, benchmark.industry_avg) : null;
  const isDemo = !location.state?.results;

  // Generate AI insights based on scores
  const generateInsights = () => {
    const weakest = [...results.dimension_scores].sort((a, b) => a.score - b.score)[0];
    const strongest = [...results.dimension_scores].sort((a, b) => b.score - a.score)[0];
    
    const executiveSummaryEn = `Your organization shows ${maturity.level.toLowerCase()} AI for HR with an overall score of ${overallScore}%. ${
      overallScore >= 60 
        ? 'You have a solid foundation for AI adoption in HR.' 
        : 'There are significant opportunities to improve your AI readiness.'
    } Focus areas include ${weakest?.dimension || 'data governance'} where improvement is most needed.`;
    
    const executiveSummaryNl = `Uw organisatie toont ${maturity.level.toLowerCase()} AI-volwassenheid met een totaalscore van ${overallScore}%. ${
      overallScore >= 60 
        ? 'U heeft een solide basis voor AI-adoptie in HR.' 
        : 'Er zijn significante mogelijkheden om uw AI-gereedheid te verbeteren.'
    } Focusgebieden omvatten ${weakest?.dimension || 'data governance'} waar verbetering het meest nodig is.`;

    const urgentActionEn = weakest?.score < 50 
      ? `Your ${weakest?.dimension} score of ${weakest?.score}% indicates this area needs immediate attention. Consider starting with foundational initiatives to build capability.`
      : null;
    
    const urgentActionNl = weakest?.score < 50 
      ? `Uw ${weakest?.dimension} score van ${weakest?.score}% geeft aan dat dit gebied onmiddellijke aandacht nodig heeft. Overweeg te beginnen met fundamentele initiatieven om capaciteit op te bouwen.`
      : null;
    
    return {
      executiveSummary: language === 'nl' ? executiveSummaryNl : executiveSummaryEn,
      urgentAction: language === 'nl' ? urgentActionNl : urgentActionEn,
      keyStrengths: [
        language === 'nl' 
          ? `${strongest?.dimension} is uw sterkste gebied met ${strongest?.score}%.`
          : `${strongest?.dimension} is your strongest area at ${strongest?.score}%.`,
        overallScore >= 50 
          ? (language === 'nl' ? 'Bovengemiddelde gereedheid voor AI-adoptie.' : 'Above-average readiness for AI adoption.')
          : (language === 'nl' ? 'Duidelijk begrip van huidige staat.' : 'Clear understanding of current state.'),
        benchmark 
          ? (language === 'nl' ? `Vergeleken met ${benchmark.total_assessments}+ organisaties in onze database.` : `Compared to ${benchmark.total_assessments}+ organizations in our database.`)
          : (language === 'nl' ? 'Uitgebreide quickscan voltooid.' : 'Comprehensive quickscan completed.'),
      ],
      recommendedActions: [
        {
          title: t('startWithQuickWins'),
          description: t('quickWinsDesc'),
          priority: t('high'),
        },
        {
          title: t('improveDimension', { dimension: weakest?.dimension }),
          description: t('improveDimensionDesc'),
          priority: weakest?.score < 40 ? t('urgent') : t('medium'),
        },
        {
          title: t('buildGovernance'),
          description: t('buildGovernanceDesc'),
          priority: t('medium'),
        },
        {
          title: t('upskillTeam'),
          description: t('upskillTeamDesc'),
          priority: t('medium'),
        },
      ],
      nextSteps: [
        { text: t('bookConsultation'), link: null },
        { text: t('exploreUseCaseHub'), link: '/use-cases' },
      ],
    };
  };

  const insights = generateInsights();

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Success Banner with animation */}
          <div className={`bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-6 mb-8 text-white transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
          }`}>
            <div className="flex items-center gap-4">
              <div className={`w-16 h-16 bg-white/20 rounded-full flex items-center justify-center transition-transform duration-500 ${
                isVisible ? 'scale-100' : 'scale-0'
              }`} style={{ transitionDelay: '200ms' }}>
                <CheckCircle2 className="h-8 w-8" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">{t('assessmentComplete')}</h1>
                <p className="text-green-100">{t('thanksForCompleting')}</p>
              </div>
            </div>
          </div>

          {/* PDF Report Content */}
          <div ref={reportRef}>
            {/* Your Results Section with staggered animation */}
            <div className={`bg-white rounded-2xl shadow-sm p-8 mb-6 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`} style={{ transitionDelay: '200ms' }}>
              <div className="flex items-center gap-3 mb-6">
                <img src="/humanistix-ai-logo.png" alt="Humanistix AI" className="h-8 w-auto" />
                <h2 className="text-2xl font-bold text-gray-900">{t('yourResults')}</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Overall Score with count-up animation */}
                <div className={`bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 text-center transition-all duration-500 ${
                  isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
                }`} style={{ transitionDelay: '400ms' }}>
                  <p className="text-gray-600 mb-2">{t('aiMaturityScore')}</p>
                  <div className="text-5xl font-bold text-blue-500 mb-2 tabular-nums">{displayScore}%</div>
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${maturity.bg} ${maturity.color} transition-all duration-300`}>
                    {maturity.level}
                  </span>
                </div>

                {/* Industry Benchmark */}
                <div className={`bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 text-center transition-all duration-500 ${
                  isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
                }`} style={{ transitionDelay: '500ms' }}>
                  <p className="text-gray-600 mb-2">{t('industryBenchmark')}</p>
                  <div className="text-5xl font-bold text-blue-500 mb-2">
                    {loadingBenchmark ? '...' : `${benchmark?.industry_avg || 58}%`}
                  </div>
                  <span className="text-sm text-gray-500">
                    {t('basedOnAssessments', { count: benchmark?.total_assessments || 200 })}
                  </span>
                </div>

                {/* Comparison */}
                <div className={`bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 text-center transition-all duration-500 ${
                  isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
                }`} style={{ transitionDelay: '600ms' }}>
                  <p className="text-gray-600 mb-2">{t('vsBenchmark')}</p>
                  {benchmarkComparison && (
                    <>
                      <div className="flex justify-center mb-2">
                        <benchmarkComparison.icon className={`h-12 w-12 ${benchmarkComparison.color}`} />
                      </div>
                      <span className={`text-sm font-medium ${benchmarkComparison.color}`}>
                        {benchmarkComparison.text}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Bell Curve Percentile Indicator */}
            <div className={`bg-white rounded-2xl shadow-sm p-8 mb-6 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`} style={{ transitionDelay: '400ms' }}>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('howYouCompare')}</h2>
              <BellCurveIndicator 
                percentile={calculatePercentile(overallScore, benchmark?.industry_avg || 58)} 
                label={t('yourOrganization')}
              />
            </div>

            {/* Spider Chart Overview */}
            <div className={`bg-white rounded-2xl shadow-sm p-8 mb-6 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`} style={{ transitionDelay: '600ms' }}>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('maturityOverview')}</h2>
              <p className="text-gray-500 mb-6">
                {t('visualRepresentation')}
              </p>
              
              {/* Spider Chart - Full Width */}
              <div className="flex justify-center mb-8">
                <div className="w-full max-w-md">
                  <SpiderChart 
                    dimensions={results.dimension_scores}
                    maxScore={100}
                    size={450}
                  />
                </div>
              </div>
              
              {/* Score Breakdown Table - Below Chart */}
              <div className="max-w-2xl mx-auto">
                <div className="bg-gray-50 rounded-xl p-5">
                  <h3 className="font-semibold text-gray-900 mb-4">{t('scoreBreakdown')}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {results.dimension_scores.map((dim) => {
                      const dimBenchmark = benchmark?.dimension_benchmarks?.[dim.dimension];
                      return (
                        <div key={dim.dimension} className="flex items-center justify-between bg-white rounded-lg px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-3 h-3 rounded-full ${
                              dim.score >= 75 ? 'bg-green-500' :
                              dim.score >= 50 ? 'bg-blue-500' :
                              'bg-red-500'
                            }`} />
                            <span className="text-gray-700 font-medium text-sm">{dim.dimension}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`font-bold ${
                              dim.score >= 75 ? 'text-green-600' :
                              dim.score >= 50 ? 'text-blue-600' :
                              'text-red-600'
                            }`}>
                              {dim.score}%
                            </span>
                            {dimBenchmark && (
                              <span className="text-xs text-gray-400">
                                ({t('avg')}: {dimBenchmark.avg})
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Overall Score */}
                  <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
                    <span className="font-bold text-gray-900">{t('totalScore')}</span>
                    <span className="text-2xl font-bold text-blue-500">{overallScore}/100</span>
                  </div>
                </div>
              </div>
            </div>

            {/* AI Analysis Report */}
            <div className="bg-white rounded-2xl shadow-sm p-8 mb-6">
              <div className="flex items-center gap-3 mb-6">
                <Sparkles className="h-6 w-6 text-purple-500" />
                <h2 className="text-2xl font-bold text-gray-900">{t('aiGeneratedReport')}</h2>
              </div>

              {/* Executive Summary */}
              <div className="bg-purple-50 rounded-xl p-6 mb-6">
                <h3 className="font-bold text-purple-900 mb-2 flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  {t('executiveSummary')}
                </h3>
                <p className="text-purple-800">{insights.executiveSummary}</p>
              </div>

              {/* Urgent Action */}
              {insights.urgentAction && (
                <div className="bg-red-50 border-l-4 border-red-500 rounded-r-xl p-6 mb-6">
                  <h3 className="font-bold text-red-900 mb-2 flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5" />
                    {t('urgentAction')}
                  </h3>
                  <p className="text-red-800">{insights.urgentAction}</p>
                </div>
              )}

              {/* Key Strengths */}
              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <h3 className="font-bold text-green-900 mb-3 flex items-center gap-2">
                  <Target className="h-5 w-5" />
                  {t('keyStrengths')}
                </h3>
                <ul className="space-y-2">
                  {insights.keyStrengths.map((strength, index) => (
                    <li key={typeof strength === 'string' ? strength : `str-${index}`} className="flex items-start gap-2 text-green-800">
                      <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>{strength}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Recommended Actions */}
              <div className="mb-6">
                <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Lightbulb className="h-5 w-5 text-yellow-500" />
                  {t('recommendedActions')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {insights.recommendedActions.map((action, index) => (
                    <div key={action.title || `act-${index}`} className="border border-gray-200 rounded-xl p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-semibold text-gray-900">{action.title}</h4>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          action.priority === t('urgent') ? 'bg-red-100 text-red-700' :
                          action.priority === t('high') ? 'bg-blue-100 text-blue-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {action.priority}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">{action.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Next Steps */}
              <div className="bg-blue-50 rounded-xl p-6">
                <h3 className="font-bold text-blue-900 mb-3 flex items-center gap-2">
                  <ArrowRight className="h-5 w-5" />
                  {t('freeNextSteps')}
                </h3>
                <ol className="space-y-2">
                  {insights.nextSteps.map((step, index) => (
                    <li key={typeof step === 'string' ? step : `step-${index}`} className="flex items-start gap-3 text-blue-800">
                      <img src="/humanistix-ai-logo.png" alt="" className="w-5 h-5 object-contain flex-shrink-0 mt-0.5" />
                      {step.link ? (
                        <a 
                          href={step.link} 
                          className="hover:text-blue-600 hover:underline transition-colors cursor-pointer"
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(step.link);
                          }}
                        >
                          {step.text}
                        </a>
                      ) : (
                        <span>{step.text}</span>
                      )}
                    </li>
                  ))}
                </ol>
              </div>
            </div>

            {/* Report Footer for PDF */}
            <div className="text-center text-sm text-gray-500 mb-6 print-only">
              <div className="flex items-center justify-center gap-2 mb-2">
                <img src="/humanistix-ai-logo.png" alt="Humanistix AI" className="h-8 w-auto" />
              </div>
              <p>AI for HR Quickscan | Generated on {new Date().toLocaleDateString()}</p>
              <p className="text-xs mt-1">www.humanistix.com | Free Quickscan Report</p>
            </div>
          </div>

          {/* Download & Action Buttons */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl p-8 text-white text-center">
            <img src="/humanistix-ai-logo.png" alt="Humanistix AI" className="h-8 w-auto mx-auto mb-4 brightness-0 invert" />
            <h2 className="text-2xl font-bold mb-2">{t('receiveFreeReport')}</h2>
            <p className="text-white/80 mb-6">{t('downloadDescription')}</p>
            
            <Button
              onClick={generatePDF}
              disabled={generatingPdf}
              className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-6 text-lg font-semibold rounded-xl"
            >
              {generatingPdf ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  {t('generatingPdf')}
                </>
              ) : (
                <>
                  <Download className="mr-2 h-5 w-5" />
                  {t('downloadPdfReport')}
                </>
              )}
            </Button>
          </div>

          {/* Register benefits CTA - only for anonymous visitors */}
          {!isAuthenticated && (
            <div className="mt-8 bg-white rounded-2xl border-2 border-blue-200 shadow-sm p-8" data-testid="register-benefits-card">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <UserPlus className="h-5 w-5 text-blue-600" />
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">
                  {language === 'nl' ? 'Maak een gratis account en bewaar je resultaten' : 'Create a free account and save your results'}
                </h2>
              </div>
              <p className="text-gray-600 mb-5">
                {language === 'nl'
                  ? 'Registreer met hetzelfde e-mailadres en deze quickscan wordt automatisch aan je account gekoppeld.'
                  : 'Register with the same email address and this quickscan is automatically linked to your account.'}
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                {(language === 'nl'
                  ? [
                      'Deze quickscan direct in je persoonlijk dashboard',
                      'Volg je AI-maturity evolutie met meerdere scans',
                      'Bewaar favoriete use cases voor later',
                      'Bekijk je resultaten en historie altijd terug',
                    ]
                  : [
                      'This quickscan instantly in your personal dashboard',
                      'Track your AI maturity evolution with multiple scans',
                      'Save favorite use cases for later',
                      'Revisit your results and history anytime',
                    ]
                ).map((benefit) => (
                  <li key={benefit} className="flex items-start gap-2 text-gray-700">
                    <CheckCircle2 className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <Button
                  onClick={() => navigate('/login', { state: { mode: 'register', email: userInfo?.email } })}
                  className="w-full sm:w-auto bg-blue-500 hover:bg-blue-600 px-8 py-6 text-lg font-semibold rounded-xl"
                  data-testid="register-free-account-btn"
                >
                  <UserPlus className="mr-2 h-5 w-5" />
                  {language === 'nl' ? 'Maak gratis account' : 'Create free account'}
                </Button>
                <span className="text-sm text-gray-500">
                  {language === 'nl' ? '100% gratis · Geen creditcard nodig' : '100% free · No credit card required'}
                </span>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex flex-col sm:flex-row gap-4 mt-8">
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="flex-1 py-6 rounded-xl"
            >
              <ArrowLeft className="mr-2 h-5 w-5" />
              {t('backToHome')}
            </Button>
            {isAuthenticated ? (
              <Button
                onClick={() => navigate('/use-cases')}
                className="flex-1 bg-blue-500 hover:bg-blue-600 py-6 rounded-xl"
                data-testid="explore-usecases-btn"
              >
                {t('exploreUseCasesBtn')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            ) : (
              <Button
                onClick={() => navigate('/login')}
                className="flex-1 bg-blue-500 hover:bg-blue-600 py-6 rounded-xl"
                data-testid="login-to-unlock-btn"
              >
                <LogIn className="mr-2 h-5 w-5" />
                {t('loginToUnlock')}
              </Button>
            )}
          </div>
        </div>
      </main>
      
      <Footer />

      {/* Tombola Modal */}
      <TombolaModal
        isOpen={showTombola}
        onClose={() => setShowTombola(false)}
        userInfo={{
          email: userInfo.email,
          name: userInfo.name,
          company: userInfo.companyName,
          companySize: userInfo.companySize,
          jobTitle: userInfo.jobTitle
        }}
        assessmentId={results?.id}
        forceVisualType={tombolaVisualType}
      />

      {/* Tombola Floating Button */}
      {!showTombola && results?.id && (
        <button
          onClick={() => setShowTombola(true)}
          className="fixed bottom-6 left-6 bg-gradient-to-r from-blue-500 to-amber-500 text-white px-5 py-3 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-105 flex items-center gap-2 z-40"
          data-testid="tombola-open-btn"
        >
          <Gift className="w-5 h-5" />
          <span className="font-medium">{language === 'nl' ? 'Speel Tombola!' : 'Play Tombola!'}</span>
        </button>
      )}

      <style>{`
        @media print {
          .print-only { display: block !important; }
        }
        @media screen {
          .print-only { display: none; }
        }
      `}</style>
    </div>
  );
};

export default AssessmentResults;
